import { useState, useEffect } from 'react';
import tmdbService from '../services/tmdbApi';

const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'just now';
    
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch now playing movies and popular TV shows for better reliability
      const [nowPlayingMovies, popularTVShows] = await Promise.all([
        tmdbService.getNowPlayingMovies({ page: 1 }),
        tmdbService.getPopularTVShows({ page: 1 })
      ]);

      // Create notification items with smaller image sizes to match original UI
      const movieNotifications = nowPlayingMovies && nowPlayingMovies.results && nowPlayingMovies.results.length > 0 
        ? nowPlayingMovies.results.slice(0, 2).map(movie => ({
            id: movie.id,
            title: movie.title,
            type: 'movie',
            poster_path: movie.poster_path,
            imgSrc: movie.poster_path ? `https://image.tmdb.org/t/p/w92${movie.poster_path}` : null,
            release_date: movie.release_date,
            timeAgo: formatTimeAgo(movie.release_date),
            link: `/movie-details/${movie.id}`
          }))
        : [];

      const tvNotifications = popularTVShows && popularTVShows.results && popularTVShows.results.length > 0 
        ? popularTVShows.results.slice(0, 1).map(tvShow => ({
            id: tvShow.id,
            title: tvShow.title,
            type: 'tv',
            poster_path: tvShow.poster_path,
            imgSrc: tvShow.poster_path ? `https://image.tmdb.org/t/p/w92${tvShow.poster_path}` : null,
            release_date: tvShow.release_date,
            timeAgo: formatTimeAgo(tvShow.release_date),
            link: `/show-details/${tvShow.id}`
          }))
        : [];

      // Combine and sort by release date (most recent first)
      const allNotifications = [...movieNotifications, ...tvNotifications]
        .filter(item => item.title) // Filter out items without titles
        .sort((a, b) => new Date(b.release_date || 0) - new Date(a.release_date || 0))
        .slice(0, 3); // Limit to 3 notifications

      setNotifications(allNotifications);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message);
      // Set fallback notifications in case of error
      setNotifications([
        {
          id: 550,
          title: 'Fight Club',
          type: 'movie',
          imgSrc: '/static/media/notify03.jpg',
          timeAgo: 'just now',
          link: '/movie-details/550'
        },
        {
          id: 13,
          title: 'Forrest Gump',
          type: 'movie',
          imgSrc: '/static/media/notify02.jpg',
          timeAgo: '15 minutes ago',
          link: '/movie-details/13'
        },
        {
          id: 155,
          title: 'The Dark Knight',
          type: 'movie',
          imgSrc: '/static/media/notify01.jpg',
          timeAgo: '1 hour ago',
          link: '/movie-details/155'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const refreshNotifications = () => {
    fetchNotifications();
  };

  return {
    notifications,
    loading,
    error,
    refreshNotifications
  };
};

export default useNotifications;
